import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, LogOut, MessageSquareText, Users, Megaphone, Smartphone, GraduationCap, Paperclip, X, LogIn, Clock, UserX, Car, ChevronRight, ArrowLeft, Layers, CalendarDays, CalendarCheck2, Check, FileSignature, StickyNote, BookOpenCheck, FolderOpen, ClipboardList } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUrlState } from "@/lib/useUrlState";
import ChatThread, { readFileAsAttachment } from "@/components/ChatThread";
import CalendarPage from "@/pages/admin/CalendarPage";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { DistrictNoticeBanner } from "@/components/DistrictNoticeBanner";
import { FormsManager } from "@/components/FormsManager";
import { FilesManager } from "@/components/docs/FilesManager";
import { DailyLogPanel } from "@/components/daycare/DailyLogPanel";
import { TeacherExcuseNotes } from "@/components/TeacherExcuseNotes";
import { NotesPanel } from "@/components/notes/NotesPanel";
import { GradebookPanel } from "@/components/gradebook/GradebookGrid";
import UserMenu from "@/components/UserMenu";
import { ScheduleWeek } from "@/components/ScheduleWeek";
import { ScheduleMonth } from "@/components/ScheduleMonth";
import { InviteResultDialog } from "@/components/InviteResultDialog";
import { HipaaAckBanner } from "@/components/HipaaAckBanner";

export default function TeacherPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useUrlState("tab", "classes");
  const [, setParams] = useSearchParams();
  const goTab = (t) => setParams(t === "classes" ? {} : { tab: t }, { replace: true });
  const [msgUnread, setMsgUnread] = useState(0);
  const [isDaycare, setIsDaycare] = useState(false);
  useEffect(() => { api.get("/me/company").then((r) => setIsDaycare(r.data?.type === "daycare")).catch(() => {}); }, []);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/messages/unread-count");
        if (alive) setMsgUnread(data.count || 0);
      } catch { /* not fatal */ }
    };
    poll();
    const iv = setInterval(poll, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, []);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useUrlState("class", "");
  const [attendance, setAttendance] = useState([]);
  const [attLoading, setAttLoading] = useState(false);
  const [threads, setThreads] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [activeStudentParam, setActiveStudentParam] = useUrlState("student", "");
  const activeStudent = activeStudentParam || null;
  const setActiveStudent = (id) => setActiveStudentParam(id || "");
  const [thread, setThread] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [broadcast, setBroadcast] = useState("");
  const [broadcastClassId, setBroadcastClassId] = useState("");
  const [broadcastBusy, setBroadcastBusy] = useState(false);
  const [broadcastAtt, setBroadcastAtt] = useState(null);
  const [inviteResult, setInviteResult] = useState(null);
  const [marking, setMarking] = useState(null);
  const [subjectMode, setSubjectMode] = useState(null); // null = school day | {subject_id, subject_name}
  const [scheduleView, setScheduleView] = useUrlState("view", "week");
  const [subjAtt, setSubjAtt] = useState(null);
  const [branding, setBranding] = useState(null);
  const selectedClass = useMemo(() => classes.find((c) => c.id === selectedClassId) || null, [classes, selectedClassId]);
  const setSelectedClass = useCallback((c) => setSelectedClassId(c?.id || ""), [setSelectedClassId]);

  useEffect(() => {
    api.get("/branding").then(({ data }) => setBranding(data)).catch(() => {});
  }, []);

  const loadSubjectAtt = useCallback(async (classId, subjectId) => {
    try {
      const { data } = await api.get("/teacher/subject-attendance", { params: { class_id: classId, subject_id: subjectId } });
      setSubjAtt(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    }
  }, []);

  useEffect(() => {
    if (!selectedClassId || !subjectMode) { setSubjAtt(null); return; }
    loadSubjectAtt(selectedClassId, subjectMode.subject_id);
  }, [selectedClassId, subjectMode, loadSubjectAtt]);

  const markSubject = async (s, status) => {
    setMarking(s.id);
    try {
      await api.post("/teacher/subject-attendance", {
        class_id: selectedClass.id, subject_id: subjectMode.subject_id, student_id: s.id, status,
      });
      setSubjAtt((a) => ({ ...a, students: a.students.map((x) => (x.id === s.id ? { ...x, status } : x)) }));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setMarking(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const [c, t, a] = await Promise.all([
        api.get("/classes"),
        api.get("/messages/threads"),
        api.get("/teacher/attendance"),
      ]);
      setClasses(c.data);
      setThreads(t.data);
      setAllStudents(a.data);
      setBroadcastClassId((prev) => prev || c.data[0]?.id || "");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadAttendance = useCallback(async (classId) => {
    try {
      const { data } = await api.get("/teacher/attendance", { params: { class_id: classId } });
      setAttendance(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load students");
    } finally {
      setAttLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== "teacher") { nav("/login"); return; }
    load();
    const iv = setInterval(load, 20000);
    return () => clearInterval(iv);
  }, [user, load, nav]);

  useEffect(() => {
    if (!activeStudent || loading) return;
    api.get(`/messages/threads/${activeStudent}`).then(({ data }) => setThread(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (!selectedClassId) return;
    setAttLoading(true);
    loadAttendance(selectedClassId);
    const iv = setInterval(() => loadAttendance(selectedClassId), 15000);
    return () => clearInterval(iv);
  }, [selectedClassId, loadAttendance]);

  const openChat = async (studentId, { dialog = false } = {}) => {
    setActiveStudent(studentId);
    setThread(null);
    if (dialog) setChatOpen(true);
    try {
      const { data } = await api.get(`/messages/threads/${studentId}`);
      setThread(data);
      setThreads((ts) => ts.map((t) => (t.student_id === studentId ? { ...t, unread: 0 } : t)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to open thread");
    }
  };

  const send = async (body, attachment) => {
    try {
      await api.post("/messages/send", { scope: "student", student_id: activeStudent, body, attachment: attachment || undefined });
      const { data } = await api.get(`/messages/threads/${activeStudent}`);
      setThread(data);
      return true;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Send failed");
      return false;
    }
  };

  const sendBroadcast = async (e) => {
    e.preventDefault();
    const classId = broadcastClassId || classes[0]?.id;
    if ((!broadcast.trim() && !broadcastAtt) || !classId) return;
    setBroadcastBusy(true);
    try {
      const { data } = await api.post("/messages/send", {
        scope: "class", class_id: classId, body: broadcast.trim(),
        attachment: broadcastAtt || undefined,
      });
      toast.success(`Sent to ${data.sent_to} students' parents`);
      setBroadcast("");
      setBroadcastAtt(null);
      load();
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Send failed");
    } finally {
      setBroadcastBusy(false);
    }
  };

  const invite = async (studentName, contact, extra = {}) => {
    try {
      const { data } = await api.post("/parents/invite", {
        student_id: activeStudent, phone: contact.phone, name: contact.name, email: extra.email || contact.email || "",
        origin_url: window.location.origin,
      });
      setInviteResult({ student: studentName, contact: contact.name || contact.phone, _student: studentName, _contact: contact, ...data });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invite failed");
    }
  };


  const mark = async (student, action) => {
    setMarking(student.id);
    try {
      await api.post(`/teacher/attendance/${student.id}`, { action, class_id: selectedClass?.id });
      toast.success(`${student.full_name} ${action === "check_in" ? "checked into class" : "checked out"}`);
      await loadAttendance(selectedClass.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setMarking(null);
    }
  };

  const markAbsent = async (student) => {
    if (!window.confirm(`Mark ${student.full_name} absent and alert the parents?`)) return;
    setMarking(student.id);
    try {
      const { data } = await api.post(`/teacher/attendance/${student.id}/absent`);
      toast.success(`${student.full_name} marked absent — parents alerted${data.alerts_sent ? ` (${data.alerts_sent} sent)` : ""}`);
      await loadAttendance(selectedClass.id);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setMarking(null);
    }
  };

  const presentCount = attendance.filter((a) => a.status === "in" || a.status === "tardy_office").length;

  return (
    <div className="h-screen flex bg-slate-50" data-testid="teacher-page">
      {/* Sidebar navigation */}
      <aside className="hidden md:flex w-[260px] shrink-0 flex-col bg-white border-r border-slate-200" data-testid="teacher-sidebar">
        <div className="px-6 py-6 border-b border-slate-200 flex items-center gap-3 min-w-0">
          <img src={branding?.logo_data_url || "/logerd-logo.png"} alt="" data-testid="teacher-school-logo"
               className={`h-10 w-10 object-contain ${branding?.logo_data_url ? "rounded-xl border border-slate-200 bg-white p-1" : ""}`} />
          <div className="min-w-0">
            <div className="font-display text-lg font-bold tracking-tight text-slate-900 truncate">{branding?.school_name || "Logerd"}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Teacher Portal</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
          <NavBtn active={tab === "classes"} onClick={() => goTab("classes")} Icon={Layers} label={isDaycare ? "My Rooms" : "My Courses"} testid="teacher-tab-classes" />
          <NavBtn active={tab === "schedule"} onClick={() => goTab("schedule")} Icon={CalendarDays} label="Schedule" testid="teacher-tab-schedule" />
          <NavBtn active={tab === "messages"} onClick={() => goTab("messages")} Icon={MessageSquareText} label="Messages" testid="teacher-tab-messages" badge={msgUnread} />
          <div className="pt-2 mt-2 border-t border-slate-100" />
          <NavBtn active={tab === "calendar"} onClick={() => goTab("calendar")} Icon={CalendarCheck2} label={isDaycare ? "Calendar" : "School Calendar"} testid="teacher-tab-calendar" />
          <NavBtn active={tab === "forms"} onClick={() => goTab("forms")} Icon={FileSignature} label="Forms" testid="teacher-tab-forms" />
          {!isDaycare && <NavBtn active={tab === "gradebook"} onClick={() => goTab("gradebook")} Icon={BookOpenCheck} label="Gradebook" testid="teacher-tab-gradebook" />}
          <NavBtn active={tab === "notes"} onClick={() => goTab("notes")} Icon={StickyNote} label="Notes" testid="teacher-tab-notes" />
          {isDaycare && <NavBtn active={tab === "daily"} onClick={() => goTab("daily")} Icon={ClipboardList} label="Daily Log" testid="teacher-tab-daily" />}
          <NavBtn active={tab === "files"} onClick={() => goTab("files")} Icon={FolderOpen} label="Files" testid="teacher-tab-files" />
          {user?.can_call_pickup && (
            <NavBtn active={false} onClick={() => nav("/dismissal")} Icon={Car} label="Dismissal" testid="teacher-tab-dismissal" />
          )}
        </nav>
      </aside>

      {/* Content column */}
      <div className="flex-1 flex flex-col min-w-0">
        <DistrictNoticeBanner />
        <header className="md:hidden bg-white border-b border-slate-200 px-4 py-3 shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <img src={branding?.logo_data_url || "/logerd-logo.png"} alt=""
                 className={`h-9 w-9 object-contain ${branding?.logo_data_url ? "rounded-xl border border-slate-100 bg-white p-0.5" : ""}`} />
            <div className="min-w-0">
              <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 truncate">
                {branding?.school_name || "Teacher Console"}
              </div>
              <div className="font-display font-semibold leading-tight truncate text-slate-900">{user?.name}</div>
            </div>
          </div>
          <UserMenu roleLabel="Teacher" />
        </header>
        <div className="hidden md:flex justify-end px-6 pt-3" data-testid="teacher-topbar">
          <UserMenu roleLabel="Teacher" />
        </div>
        <div className="md:hidden flex bg-white border-b border-slate-200 px-4 pb-2 gap-1 overflow-x-auto">
          <TabBtn active={tab === "classes"} onClick={() => goTab("classes")} Icon={Layers} label={isDaycare ? "My Rooms" : "My Courses"} testid="teacher-tab-classes-m" />
          <TabBtn active={tab === "schedule"} onClick={() => goTab("schedule")} Icon={CalendarDays} label="Schedule" testid="teacher-tab-schedule-m" />
          <TabBtn active={tab === "messages"} onClick={() => goTab("messages")} Icon={MessageSquareText} label="Messages" testid="teacher-tab-messages-m" badge={msgUnread} />
          <TabBtn active={tab === "calendar"} onClick={() => goTab("calendar")} Icon={CalendarCheck2} label="Calendar" testid="teacher-tab-calendar-m" />
          <TabBtn active={tab === "forms"} onClick={() => goTab("forms")} Icon={FileSignature} label="Forms" testid="teacher-tab-forms-m" />
          {!isDaycare && <TabBtn active={tab === "gradebook"} onClick={() => goTab("gradebook")} Icon={BookOpenCheck} label="Gradebook" testid="teacher-tab-gradebook-m" />}
          <TabBtn active={tab === "notes"} onClick={() => goTab("notes")} Icon={StickyNote} label="Notes" testid="teacher-tab-notes-m" />
          {isDaycare && <TabBtn active={tab === "daily"} onClick={() => goTab("daily")} Icon={ClipboardList} label="Daily Log" testid="teacher-tab-daily-m" />}
          <TabBtn active={tab === "files"} onClick={() => goTab("files")} Icon={FolderOpen} label="Files" testid="teacher-tab-files-m" />
          {user?.can_call_pickup && (
            <TabBtn active={false} onClick={() => nav("/dismissal")} Icon={Car} label="Dismissal" testid="teacher-tab-dismissal-m" />
          )}
        </div>

      <HipaaAckBanner />
      {loading ? (
        <div className="flex-1 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : tab === "gradebook" ? (
        <main className="flex-1 overflow-y-auto px-6 pb-6 pt-2" data-testid="teacher-gradebook"><GradebookPanel /></main>
      ) : tab === "notes" ? (
        <main className="flex-1 overflow-y-auto px-6 pb-6 pt-2" data-testid="teacher-notes"><NotesPanel /></main>
      ) : tab === "daily" ? (
        <main className="flex-1 overflow-y-auto px-6 pb-6 pt-4" data-testid="teacher-daily-log"><DailyLogPanel /></main>
      ) : tab === "files" ? (
        <main className="flex-1 overflow-y-auto px-6 pb-6 pt-4" data-testid="teacher-files"><FilesManager defaultScope="teacher" /></main>
      ) : tab === "forms" ? (
        <main className="flex-1 overflow-y-auto px-6 pb-6 pt-2" data-testid="teacher-forms"><FormsManager mode="teacher" /></main>
      ) : tab === "calendar" ? (
        <main className="flex-1 overflow-y-auto" data-testid="teacher-calendar-view">
          <CalendarPage embedded />
        </main>
      ) : classes.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-slate-500 px-6 text-center">
          You don't have a course yet — ask your school admin to assign you one on the Courses page.
        </div>
      ) : tab === "classes" ? (
        <main className="flex-1 overflow-y-auto px-6 pb-6 pt-2">
          {!selectedClass ? (
            <div className="w-full" data-testid="teacher-class-grid">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-1" data-testid="teacher-class-name">My Courses</div>
              <div className="font-display text-2xl font-bold tracking-tight text-slate-900 mb-4">{isDaycare ? "All my rooms" : "All my classes"}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {classes.map((c) => (
                  <button
                    key={c.id} onClick={() => setSelectedClass(c)}
                    data-testid={`teacher-class-card-${c.id}`}
                    className="rounded-xl border border-slate-200 bg-white p-5 text-left hover:shadow-md hover:border-primary/40 transition flex items-center gap-4 active:scale-[0.99]"
                    onClickCapture={() => setSubjectMode(null)}
                  >
                    <div className="h-11 w-11 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                      <GraduationCap className="h-5 w-5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-slate-900 truncate">{c.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5 inline-flex items-center gap-1">
                        <Users className="h-3 w-3" /> {c.student_count} student{c.student_count === 1 ? "" : "s"}
                      </div>
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {c.teacher_id === user?.id && (
                          <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600" data-testid={`homeroom-chip-${c.id}`}>
                            Homeroom
                          </span>
                        )}
                        {(c.my_subjects || []).map((sub) => (
                          <span key={sub} className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700" data-testid={`subject-chip-${c.id}-${sub}`}>
                            {sub}
                          </span>
                        ))}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="w-full rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid="teacher-class-students">
              <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-3">
                <button onClick={() => { setSelectedClass(null); setSubjectMode(null); }} data-testid="teacher-back-to-classes"
                        className="h-8 w-8 shrink-0 rounded-lg border border-slate-200 inline-flex items-center justify-center text-slate-600 hover:bg-slate-50 transition">
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-semibold text-slate-900 truncate">{selectedClass.name}</div>
                  <div className="text-xs text-slate-500" data-testid="attendance-present-count">
                    {subjectMode ? `${subjectMode.subject_name} · today` : `${presentCount} of ${attendance.length} present today`}
                  </div>
                </div>
              </div>
              {(selectedClass.my_assignments || []).length > 0 && (
                <div className="px-5 py-2.5 border-b border-slate-100 bg-slate-50/60 flex flex-wrap gap-1.5" data-testid="subject-mode-switch">
                  <button
                    onClick={() => setSubjectMode(null)}
                    data-testid="subject-mode-day"
                    className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                      !subjectMode ? "bg-primary text-white shadow-sm" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    School day
                  </button>
                  {selectedClass.my_assignments.map((a) => (
                    <button
                      key={a.subject_id}
                      onClick={() => setSubjectMode(a)}
                      data-testid={`subject-mode-${a.subject_id}`}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition ${
                        subjectMode?.subject_id === a.subject_id ? "bg-primary text-white shadow-sm" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-100"
                      }`}
                    >
                      {a.subject_name}
                    </button>
                  ))}
                </div>
              )}
              {!subjectMode && <TeacherExcuseNotes classId={selectedClass.id} />}
              {subjectMode ? (
                !subjAtt ? (
                  <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
                ) : (
                  <>
                    {subjAtt.students.map((s) => (
                      <div key={s.id} className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3" data-testid={`subject-att-student-${s.id}`}>
                        <div className="min-w-36 flex-1">
                          <button onClick={() => nav(`/teacher/student/${s.id}`)} data-testid={`teacher-student-name-${s.id}`} className="font-medium text-slate-900 text-sm hover:text-primary hover:underline text-left">{s.full_name}</button>
                          <div className="text-xs text-slate-400">{s.grade}</div>
                        </div>
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                          s.status === "present" ? "bg-emerald-100 text-emerald-800"
                          : s.status === "tardy" ? "bg-amber-100 text-amber-800"
                          : s.status === "absent" ? "bg-rose-100 text-rose-800"
                          : "bg-slate-100 text-slate-500"
                        }`} data-testid={`subject-att-status-${s.id}`}>
                          {s.status === "unmarked" ? "Not marked" : s.status.charAt(0).toUpperCase() + s.status.slice(1)}
                        </span>
                        <div className="flex gap-1.5">
                          <IconAction Icon={MessageSquareText} title="Message the parents" testid={`student-message-${s.id}`}
                            className="border-slate-200 text-primary hover:bg-primary/5"
                            onClick={() => openChat(s.id, { dialog: true })} />
                          <IconAction Icon={Check} title="Present" testid={`subject-present-${s.id}`}
                            busy={marking === s.id}
                            className={s.status === "present" ? "border-emerald-200 bg-emerald-600 text-white" : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"}
                            onClick={() => markSubject(s, "present")} />
                          <IconAction Icon={Clock} title="Tardy" testid={`subject-tardy-${s.id}`}
                            busy={marking === s.id}
                            className={s.status === "tardy" ? "border-amber-200 bg-amber-500 text-white" : "border-amber-200 text-amber-700 hover:bg-amber-50"}
                            onClick={() => markSubject(s, "tardy")} />
                          <IconAction Icon={UserX} title="Absent" testid={`subject-absent-${s.id}`}
                            busy={marking === s.id}
                            className={s.status === "absent" ? "border-rose-200 bg-rose-600 text-white" : "border-rose-200 text-rose-600 hover:bg-rose-50"}
                            onClick={() => markSubject(s, "absent")} />
                        </div>
                      </div>
                    ))}
                    {subjAtt.students.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No students assigned to this class yet.</div>}
                  </>
                )
              ) : attLoading && attendance.length === 0 ? (
                <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
              ) : (
                <>
                  {attendance.map((s) => (
                    <div key={s.id} className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3" data-testid={`attendance-student-${s.id}`}>
                      <div className="min-w-36 flex-1">
                        <button onClick={() => nav(`/teacher/student/${s.id}`)} data-testid={`teacher-student-name-${s.id}`} className="font-medium text-slate-900 text-sm hover:text-primary hover:underline text-left">{s.full_name}</button>
                        <div className="text-xs text-slate-400">{s.grade}</div>
                      </div>
                      <StatusChip s={s} />
                      <div className="flex gap-1.5">
                        <IconAction
                          Icon={MessageSquareText} title="Message the parents"
                          testid={`student-message-${s.id}`}
                          className="border-slate-200 text-primary hover:bg-primary/5"
                          onClick={() => openChat(s.id, { dialog: true })}
                        />
                        {s.status !== "in" && (
                          <IconAction
                            Icon={LogIn} title={s.status === "tardy_office" ? "Check into class" : "Check in"}
                            testid={`attendance-checkin-${s.id}`}
                            busy={marking === s.id}
                            className={s.status === "tardy_office"
                              ? "border-amber-200 bg-amber-500 text-white hover:bg-amber-600"
                              : "border-emerald-200 bg-emerald-600 text-white hover:bg-emerald-700"}
                            onClick={() => mark(s, "check_in")}
                          />
                        )}
                        {(s.status === "in" || s.status === "tardy_office") && (
                          <IconAction
                            Icon={LogOut} title="Check out"
                            testid={`attendance-checkout-${s.id}`}
                            busy={marking === s.id}
                            className="border-slate-200 text-slate-600 hover:bg-slate-100"
                            onClick={() => mark(s, "check_out")}
                          />
                        )}
                        {s.status === "not_arrived" && (
                          <IconAction
                            Icon={UserX} title="Mark absent — alerts the parents"
                            testid={`attendance-absent-${s.id}`}
                            busy={marking === s.id}
                            className="border-rose-200 text-rose-600 hover:bg-rose-50"
                            onClick={() => markAbsent(s)}
                          />
                        )}
                      </div>
                    </div>
                  ))}
                  {attendance.length === 0 && <div className="p-8 text-center text-sm text-slate-400">No students assigned to this class yet.</div>}
                </>
              )}
            </div>
          )}
        </main>
      ) : tab === "schedule" ? (
        <main className="flex-1 overflow-y-auto px-6 pb-6 pt-2">
          <div className="w-full" data-testid="teacher-schedule">
            <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-1">My Schedule</div>
                <div className="font-display text-2xl font-bold tracking-tight text-slate-900">{scheduleView === "week" ? "Week" : "Month"}</div>
              </div>
              <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5" data-testid="teacher-schedule-view">
                {["week", "month"].map((v) => (
                  <button key={v} type="button" onClick={() => setScheduleView(v)} data-testid={`teacher-schedule-view-${v}`}
                    className={`h-8 rounded-md px-3 text-xs font-semibold capitalize transition ${scheduleView === v ? "bg-primary text-white shadow-sm" : "text-slate-600 hover:bg-slate-100"}`}>{v}</button>
                ))}
              </div>
            </div>
            {classes.some((c) => c.period) ? (
              scheduleView === "week"
                ? <ScheduleWeek classes={classes.filter((c) => c.period)} onSelect={(c) => { setSubjectMode(null); setSelectedClass(c); setTab("classes"); }} testid="teacher-schedule-week" />
                : <ScheduleMonth classes={classes.filter((c) => c.period)} onSelect={(c) => { setSubjectMode(null); setSelectedClass(c); setTab("classes"); }} testid="teacher-schedule-month" />
            ) : (
              <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500" data-testid="teacher-schedule-empty">
                No scheduled periods yet — your admin can assign you periods under Employees → your profile → Schedule.
              </div>
            )}
          </div>
        </main>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-[340px_1fr]">
          <aside className={`border-r border-slate-200 bg-white overflow-y-auto ${activeStudent && !chatOpen ? "hidden md:block" : ""}`} data-testid="teacher-threads">
            <form onSubmit={sendBroadcast} className="p-4 border-b border-slate-100 space-y-2 bg-amber-50/50">
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-amber-800">
                <Megaphone className="h-3.5 w-3.5" /> Message whole class
              </div>
              {classes.length > 1 && (
                <select
                  value={broadcastClassId} onChange={(e) => setBroadcastClassId(e.target.value)}
                  data-testid="broadcast-class-select"
                  className="w-full h-9 rounded-lg border border-amber-200 bg-white px-2 text-sm outline-none"
                >
                  {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              )}
              <textarea
                value={broadcast} onChange={(e) => setBroadcast(e.target.value)} rows={2}
                data-testid="teacher-broadcast-input"
                placeholder={`To all parents of ${classes.find((c) => c.id === broadcastClassId)?.name || classes[0]?.name}…`}
                className="w-full resize-none rounded-lg border border-amber-200 bg-white px-3 py-2 text-sm focus:border-amber-400 outline-none"
              />
              {broadcastAtt && (
                <span className="inline-flex items-center gap-2 rounded-full bg-amber-100 text-amber-800 px-3 py-1 text-xs font-medium" data-testid="broadcast-attachment-chip">
                  <Paperclip className="h-3 w-3" /> {broadcastAtt.name}
                  <button type="button" onClick={() => setBroadcastAtt(null)}><X className="h-3 w-3" /></button>
                </span>
              )}
              <div className="flex gap-2">
                <label className="h-9 w-9 shrink-0 rounded-lg border border-amber-200 bg-white text-amber-700 inline-flex items-center justify-center cursor-pointer hover:bg-amber-100 transition" title="Attach permission slip / photo">
                  <Paperclip className="h-4 w-4" />
                  <input
                    type="file" accept="image/*,application/pdf" className="hidden" data-testid="broadcast-file-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      if (f) readFileAsAttachment(f, setBroadcastAtt, (err) => toast.error(err));
                    }}
                  />
                </label>
                <button
                  type="submit" disabled={broadcastBusy || (!broadcast.trim() && !broadcastAtt)} data-testid="teacher-broadcast-send"
                  className="flex-1 h-9 rounded-lg bg-amber-600 text-white text-sm font-medium inline-flex items-center justify-center gap-2 hover:bg-amber-700 disabled:opacity-50 transition"
                >
                  {broadcastBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Megaphone className="h-4 w-4" />} Send to class
                </button>
              </div>
            </form>
            {allStudents.map((s) => {
              const t = threads.find((th) => th.student_id === s.id);
              return (
                <button
                  key={s.id} onClick={() => openChat(s.id)}
                  data-testid={`teacher-thread-${s.id}`}
                  className={`w-full text-left px-4 py-3 border-b border-slate-100 hover:bg-slate-50 transition ${activeStudent === s.id ? "bg-primary/5" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-slate-900 text-sm truncate">{s.full_name}</span>
                    {t?.unread > 0 && <span className="shrink-0 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white">{t.unread}</span>}
                  </div>
                  <div className="text-xs text-slate-400 truncate mt-0.5">{t?.last_preview || "No messages yet"}</div>
                </button>
              );
            })}
            {allStudents.length === 0 && <div className="p-6 text-center text-sm text-slate-400">No students in your classes yet.</div>}
          </aside>
          <section className={`min-h-0 flex flex-col ${activeStudent ? "" : "hidden md:flex"}`}>
            {activeStudent && thread && !chatOpen ? (
              <>
                <div className="bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3">
                  <button className="md:hidden text-xs text-primary" onClick={() => setActiveStudent(null)}>← Back</button>
                  <div className="h-8 w-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><GraduationCap className="h-4 w-4" /></div>
                  <div>
                    <div className="text-sm font-semibold text-slate-900" data-testid="teacher-chat-student">{thread.student.full_name}</div>
                    <div className="text-[11px] text-slate-400">{thread.student.contacts.map((c) => c.name).filter(Boolean).join(", ") || "No parent contacts"}</div>
                  </div>
                </div>
                <div className="flex-1 min-h-0">
                  <ChatThread messages={thread.messages} onSend={send} mySenderType="staff" emptyText="No messages yet — say hello to the parents." />
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-400">Pick a student to open the conversation.</div>
            )}
          </section>
        </div>
      )}
      </div>

      <Dialog open={chatOpen} onOpenChange={(o) => { if (!o) { setChatOpen(false); setActiveStudent(null); setThread(null); } }}>
        <DialogContent className="sm:max-w-2xl p-0 gap-0 overflow-hidden" data-testid="student-chat-dialog">
          <DialogHeader className="px-4 py-3 border-b border-slate-100">
            <DialogTitle className="font-display flex items-center gap-2 text-base">
              <MessageSquareText className="h-4.5 w-4.5 text-primary" />
              {thread?.student?.full_name || "Loading…"}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {(thread?.student?.contacts || []).filter((c) => c.phone).map((c, i) => (
                  <button
                    key={i} type="button" onClick={() => invite(thread.student.full_name, c)}
                    data-testid={`chat-invite-${i}`}
                    className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition ${
                      c.app_status === "active"
                        ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                        : c.app_status === "invited" ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
                        : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                    title={c.app_status === "active" ? "Has the parent app" : c.app_status === "invited" ? "Invited — click to resend" : "Send parent app invite"}
                  >
                    <Smartphone className="h-3 w-3" /> {c.name || c.phone}{c.app_status === "active" ? " · on app" : c.app_status === "invited" ? " · invited — resend" : " · invite"}
                  </button>
                ))}
                {thread && (thread.student.contacts || []).filter((c) => c.phone).length === 0 && (
                  <span className="text-[11px] text-slate-400">No parent phone on file</span>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="h-[55vh]">
            {thread ? (
              <ChatThread messages={thread.messages} onSend={send} mySenderType="staff" emptyText="No messages yet — say hello to the parents." />
            ) : (
              <div className="h-full flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <InviteResultDialog result={inviteResult} onClose={() => setInviteResult(null)}
        onResend={(extra) => invite(inviteResult._student, inviteResult._contact, extra)} />
    </div>
  );
}

function IconAction({ Icon, title, onClick, testid, className = "", busy = false }) {
  return (
    <button
      onClick={onClick} disabled={busy} data-testid={testid} title={title} aria-label={title}
      className={`h-9 w-9 rounded-lg border inline-flex items-center justify-center transition disabled:opacity-60 ${className}`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
    </button>
  );
}

function StatusChip({ s }) {
  const time = s.latest_at ? new Date(s.latest_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "";
  if (s.status === "tardy_office") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-semibold text-amber-800" data-testid={`attendance-status-${s.id}`}>
        <Clock className="h-3 w-3" /> Tardy · reported at office {time}
      </span>
    );
  }
  if (s.status === "in") {
    return (
      <span className="inline-flex items-center gap-1 flex-wrap">
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-[11px] font-semibold text-emerald-800" data-testid={`attendance-status-${s.id}`}>
          <LogIn className="h-3 w-3" /> {s.period_mode ? "In class" : "In"} · {time}{s.latest_officer && !s.period_mode ? ` by ${s.latest_officer}` : ""}
        </span>
        {s.late && <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-[11px] font-semibold text-amber-800" data-testid={`attendance-late-${s.id}`}><Clock className="h-3 w-3" /> Late</span>}
      </span>
    );
  }
  if (s.status === "absent") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2.5 py-1 text-[11px] font-semibold text-rose-800" data-testid={`attendance-status-${s.id}`}>
        <UserX className="h-3 w-3" /> Absent · parent alerted
      </span>
    );
  }
  if (s.status === "out") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-[11px] font-semibold text-blue-800" data-testid={`attendance-status-${s.id}`}>
        <LogOut className="h-3 w-3" /> Out · {time}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600" data-testid={`attendance-status-${s.id}`}>
      Not arrived
    </span>
  );
}

function TabBtn({ active, onClick, Icon, label, testid, badge = 0 }) {
  return (
    <button
      onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium whitespace-nowrap transition ${
        active ? "bg-primary/10 text-primary" : "text-slate-500 hover:text-slate-900"
      }`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
      {badge > 0 && <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">{badge > 99 ? "99+" : badge}</span>}
    </button>
  );
}

function NavBtn({ active, onClick, Icon, label, testid, badge = 0 }) {
  return (
    <button
      onClick={onClick} data-testid={testid}
      className={`w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition ${
        active ? "bg-primary/10 text-primary" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`}
    >
      <Icon className="h-4 w-4" /> <span className="flex-1 text-left">{label}</span>
      {badge > 0 && (
        <span data-testid={`${testid}-badge`} className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-bold text-white">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );
}

